import React, { useCallback, useMemo } from 'react'
import { ReactComponent as GoogleIcon } from './../../../../images/icons/audience_google.svg'
import { ReactComponent as BingIcon } from './../../../../images/icons/audience_bing.svg'
import { ReactComponent as BasisLogo } from '../../../../images/icons/audience_basis.svg'
import { ReactComponent as MailchimpIcon } from '../../../../images/icons/audience_mailchimp.svg'
import { ReactComponent as FacebookIcon } from './../../../../images/icons/audience_facebook.svg'
import { ReactComponent as TradeDeskIcon } from './../../../../images/icons/audience_tradedesk.svg'
import { ReactComponent as BrevoIcon } from './../../../../images/icons/audience_brevo.svg'
import { ReactComponent as AmazonIcon } from './../../../../images/icons/audience_amazon.svg'
import { ReactComponent as ActionDelete } from './../../../../images/icons/action_delete.svg'
import { ReactComponent as ActionDeploy } from './../../../../images/icons/action_deploy.svg'
import { ReactComponent as ActionDownload } from './../../../../images/icons/action_download.svg'
import { ReactComponent as ActionEdit } from '../../../../images/icons/action_edit.svg'
import { motion } from 'framer-motion'
import { formatNumber } from '../../../../lib/utils/formatNumber'
import { useModal } from '../../../../components/useModal'
import DeleteAudienceModal from '../../../../forms/DeleteAudience/DeleteAudienceModal'
import { Audience } from '../../../../lib/types/Audience'
import { useDownloadAudienceByAudienceId } from '../../../../api/dealership/customers/audiences/useDownloadAudienceByAudienceId'
import dayjs from 'dayjs'
import { ColumnDefBase, createColumnHelper } from '@tanstack/react-table'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import EditAudience from '../../../../forms/EditAudience/EditAudience'
import { isAfter } from 'date-fns'
import toast from 'react-hot-toast'
import EmptyAudiencesList from '../../../../components/Audiences/EmptyAudiencesList'
import LoadingAnimation from '../../../../components/LoadingAnimation'
import { ActionRefresh, Button, LoadingComponent, Table } from '@demandlocal/components'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../../../store/selectors/userSelector'
import { usePostResyncAudience } from '../../../../api/admin/usePostResyncAudience'
import { useGetAudiences } from '../../../../api/dealership/customers/audiences/useGetAudiences'
import { usePostRefreshAudience } from '../../../../api/dealership/audiences/usePostRefreshAudience'
import { useGetOverallStats } from '../../../../api/dealership/useGetOverallStats'
import { ReactComponent as AddCustomersImage } from '../../../../images/add_customers.svg'
import { BuildAudienceGuide } from '../../../../components/Audiences/BuildAudienceGuide'

const columnHelper = createColumnHelper<
  Audience & { mappedAudiences: Record<string, number> }
>()

const MyAudiences = (): JSX.Element => {
  const currentUser = useSelector(currentUserSelector)
  const { mutateAsync: resyncAudience } = usePostResyncAudience()
  const { mutateAsync: refreshAudience } = usePostRefreshAudience()
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const { data } = useGetAudiences({})

  const { handleModal } = useModal()
  const navigate = useNavigate()

  const { mutate, isLoading } = useDownloadAudienceByAudienceId()

  const { data: audiences, isLoading: isLoadingAudiences } = useGetAudiences({})

  const onResyncAudience = useCallback(
    async (audienceId: string): Promise<void> => {
      await resyncAudience({ data: { audience_id: audienceId } })
      toast.success('Audience will be synced in the background.')
    },
    [resyncAudience],
  )
  const onRefreshAudience = useCallback(
    async (audienceId: string) => {
      await refreshAudience({ data: { audience_id: audienceId } })
      toast.success('Audience will be refreshed in the background.')
    },
    [refreshAudience],
  )

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('audience_name', {
          header: 'Name',
          cell: (info) => (
            <Link
              to={`details/${info.row.original._id}`}
              className={'text-primary-persian-blue font-bold'}>
              {info.row.original.audience_name}
            </Link>
          ),
          enableSorting: false,
        }),
        columnHelper.accessor('description', {
          header: 'Description',
          cell: (info) => info.getValue() || '-',
          enableSorting: false,
        }),
        columnHelper.accessor('audience_size', {
          header: 'Audience size',
          cell: (info) => {
            return formatNumber(info.getValue() || 0)
          },
          enableSorting: false,
        }),
        columnHelper.accessor('last_run', {
          header: 'Last update',
          cell: (info) =>
            info.getValue() ? dayjs(info.getValue()).format('MMM DD, YYYY') : '-',
          enableSorting: false,
        }),
        columnHelper.display({
          id: 'linked_to',
          header: () => 'Linked to',
          cell: ({ row }) => (
            <div className={'flex flex-row gap-4'}>
              <div className={'relative'}>
                <GoogleIcon />
                {row.original.mappedAudiences?.google > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.google}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <BingIcon />
                {row.original.mappedAudiences?.bing > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.bing}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <MailchimpIcon />
                {row.original.mappedAudiences?.mailchimp > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.mailchimp}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <FacebookIcon />
                {row.original.mappedAudiences?.facebook > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.facebook}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <TradeDeskIcon />
                {row.original.mappedAudiences?.tradedesk > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.tradedesk}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <BasisLogo className={'w-6 h-6'} />
                {row.original.mappedAudiences?.basis > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.basis}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <AmazonIcon className={'w-6 h-6'} />
                {row.original.mappedAudiences?.amazon > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.amazon}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <BrevoIcon className={'w-6 h-6'} />
                {row.original.mappedAudiences?.brevo > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.brevo}
                  </span>
                )}
              </div>
            </div>
          ),
        }),
        currentUser?.role_id === 1
          ? columnHelper.display({
              id: 'created_by',
              header: 'Created By',
              enableHiding: true,
              cell: (info) => {
                const name = `${info.row.original.created_by?.first_name ?? ''} ${
                  info.row.original.created_by?.last_name ?? ''
                }`
                return name.trim()?.length > 0 ? name : '-'
              },
              enableSorting: false,
            })
          : null,
        columnHelper.display({
          id: 'actions',
          header: () => 'Actions',
          cell: ({ row }) => {
            const isNewAudienceType = isAfter(
              new Date(row.original.createdAt),
              new Date(2023, 0, 16),
            )
            return (
              <div className={'flex flex-row gap-2 items-center'}>
                {currentUser?.role_id === 1 && (
                  <ActionRefresh
                    // @ts-ignore
                    className={`cursor-pointer stroke-primary-persian-blue  fill-primary-persian-blue`}
                    onClick={() => {
                      if (
                        confirm(
                          'Are you sure you want to refresh this audience? This will add new members and remove outdated ones.',
                        )
                      )
                        onRefreshAudience(row.original._id)
                    }}
                  />
                )}
                <ActionDelete
                  className={'cursor-pointer'}
                  onClick={() =>
                    handleModal(<DeleteAudienceModal audience={row.original} />)
                  }
                />
                <ActionEdit
                  stroke={isNewAudienceType ? '#0932C2' : '#ccc'}
                  className={'cursor-pointer'}
                  onClick={(e) => {
                    e.preventDefault()
                    if (isNewAudienceType)
                      handleModal(<EditAudience audience_id={row.original._id} />)
                  }}
                />
                {dealership?.enable_audience_download && (
                  <ActionDownload
                    fill={isLoading ? '#ccc' : '#0932C2'}
                    className={`${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={() => {
                      const toastId = toast.loading('Downloading audience...')
                      mutate(
                        { audience_id: row.original._id },
                        {
                          async onSuccess(data) {
                            const blob = await data.blob()
                            const url = window.URL.createObjectURL(blob)
                            const a = document.createElement('a')
                            a.href = url
                            a.download = `${row.original.audience_name}.csv`
                            document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                            a.click()
                            a.remove()
                            toast.success('Audience downloaded', { id: toastId })
                          },
                        },
                      )
                    }}
                  />
                )}
                <Button
                  key={`${row.original._id}-push`}
                  animate={false}
                  variant="secondary"
                  onClick={() => {
                    navigate(`create/${row.original._id}/deploy`)
                  }}
                  rightSlot={
                    <ActionDeploy className={'cursor-pointer fill-primary-xiketic'} />
                  }>
                  Push
                </Button>
                {currentUser?.beta_tester && (
                  <Button
                    key={row.original._id}
                    animate={false}
                    variant="tertiary"
                    onClick={() => {
                      if (
                        confirm(
                          'Are you sure you want to re-sync this audience? This will recreate the audience and sync it with all defined destinations.',
                        )
                      )
                        onResyncAudience(row.original._id)
                    }}>
                    Re-sync
                  </Button>
                )}
              </div>
            )
          },
        }),
      ].filter((column) => column !== null),
    [
      currentUser?.role_id,
      currentUser?.beta_tester,
      dealership?.enable_audience_download,
      isLoading,
      onRefreshAudience,
      handleModal,
      mutate,
      navigate,
      onResyncAudience,
    ],
  )

  const {
    data: { totalCustomers = 0, totalAudiences = 0 } = {},
    isLoading: isLoadingOverallStats,
  } = useGetOverallStats()

  const hasNoAudiences = useMemo(
    () => !isLoadingOverallStats && totalAudiences === 0,
    [isLoadingOverallStats, totalAudiences],
  )
  const hasNoCustomers = useMemo(() => totalCustomers === 0, [totalCustomers])

  if (isLoadingOverallStats) return <LoadingComponent title={'Loading Audiences'} />

  if (hasNoAudiences && hasNoCustomers) {
    return (
      <div className="flex flex-col h-full justify-center items-center">
        <div
          className={'flex flex-col gap-5 my-auto mx-auto items-center justify-center'}>
          <AddCustomersImage className="mx-auto" />
          <span className={'font-bold text-3xl font-Spartan'}>
            You have no customers yet
          </span>
          <span className={'text-base font-Spartan'}>
            You must add customers before creating an audience
          </span>
          <div className={'w-2/3'}>
            <BuildAudienceGuide />
          </div>
          <Button
            variant="rainbow"
            onClick={() =>
              navigate(t('dealership.customers.sources.create', { dealership }))
            }>
            + Add customers
          </Button>
          <Outlet />
        </div>
      </div>
    )
  }

  return (
    <motion.div className={'flex flex-1 flex-col p-10 font-Spartan'}>
      {(data?.pagination?.totalDocs ?? 0) > 0 && (
        <div className={'w-full flex flex-col'}>
          <span className="text-md py-3">
            Build, filter, and deploy your unique customer audiences across ads types on
            many advertising platforms using the LinkOne Audience Builder. Perfect for
            creating a seed and speaking to the right customer with the right message.
          </span>
          <div className={'pb-4 flex justify-between'}>
            <div>
              <div
                className={
                  'font-Spartan font-bold text-white p-2 rounded-md text-[10px] border-primary-persian-blue'
                }>
                {formatNumber(data?.pagination?.totalDocs)} audiences
              </div>
            </div>
            <Button
              onClick={() => navigate(t('dealership.audiences.create', { dealership }))}>
              + Add Audience
            </Button>
          </div>
        </div>
      )}
      {isLoadingAudiences && (
        <div className="flex justify-center items-center w-full p-2">
          <LoadingAnimation />
        </div>
      )}
      {!isLoadingAudiences && (audiences?.pagination?.totalDocs ?? 0) > 0 && (
        <Table columns={columns} filters={[]} fetchFn={useGetAudiences} />
      )}
      {!isLoadingAudiences && audiences?.pagination?.totalDocs === 0 && (
        <EmptyAudiencesList />
      )}
      <Outlet />
    </motion.div>
  )
}

export default MyAudiences
